import { Chip } from '@/app/components/Chip'
import { HorizontalBulletList } from '@/app/components/HorizontalBulletList'
import { Headline1 } from '@/app/components/Typography'
import { ICourse } from '@/types/course'
import { useTranslations } from 'next-intl'
import { useFormatters } from '@/app/hooks/useFormatters'

type CourseHeaderProps = {
  course: Pick<
    ICourse,
    'name' | 'summary' | 'learningAreas' | 'level' | 'cohorts'
  >
} & React.HTMLAttributes<HTMLDivElement>

const MAX_LEARNING_AREAS_TO_DISPLAY = 5

export function CourseHeader({ course, ...props }: CourseHeaderProps) {
  const t = useTranslations('CoursePage')
  const { formatCourseDuration } = useFormatters()
  const { name, level, cohorts = [], summary, learningAreas = [] } = course

  const subtitleItems = [
    formatCourseDuration(course),
    ...(level ? [t(`level_${level}`)] : []),
    ...(cohorts?.length > 1 ? [t('cohortBased')] : []),
  ].filter(Boolean)

  return (
    <header {...props} className={`text-mydra-black ${props.className}`}>
      <Headline1 className="md:leading-10">{name}</Headline1>
      <HorizontalBulletList className="mt-3" items={subtitleItems} />
      {summary && (
        <p className="mt-4 text-gray-light font-title md:text-[1.25rem] lg:text-[1.375rem] md:tracking-tight md:leading-7 lg:leading-8">
          {summary}
        </p>
      )}
      {!!learningAreas.length && (
        <div className="w-full mt-8 md:mt-6">
          <ul className="flex gap-2 pr-4 -mr-8 overflow-x-auto no-scrollbar md:flex-wrap">
            {learningAreas
              .slice(0, MAX_LEARNING_AREAS_TO_DISPLAY)
              .map((learningArea, index) => (
                <li key={index} className="flex-shrink-0">
                  <Chip key={index} size="small">
                    {learningArea}
                  </Chip>
                </li>
              ))}
          </ul>
        </div>
      )}
    </header>
  )
}
